import { useMemo } from 'react'

import {
  useCreateHomepagePin,
  useDeleteHomepagePin,
  useListHomepagePins,
} from 'services/homepage'
import { useFeatureFlagService } from 'utils/useFeatureFlagService'

export type PinTarget =
  | { targetType: 'page'; pageKey: string }
  | {
      targetType:
        | 'deal'
        | 'company'
        | 'campaign'
        | 'contact'
        | 'note'
        | 'document'
      targetId: number
    }

type UsePinTargetResult = {
  isPinned: boolean
  toggle: () => void
  isPending: boolean
}

// Minimal shape a pin row needs to be matched against a target. Both the
// generated PinListItem and any equivalent row satisfy this.
type PinLike = {
  target_type: string
  page_key?: string | null
  target_id?: number | null
}

/**
 * Read the current pins list, gated on the Quick Access feature flag.
 *
 * Quick Access is flag-gated. When the flag is off the fetch is skipped
 * entirely — otherwise the backend `QuickAccessEnabled` permission rejects
 * `GET /homepage/pins/` with a 403, even though the pin UI is already hidden.
 *
 * @returns The pins list, or undefined while loading / when the flag is off.
 */
function useGatedPins() {
  const { enableQuickAccess } = useFeatureFlagService()
  return useListHomepagePins({ query: { enabled: enableQuickAccess } })
}

/**
 * Whether a pin row refers to the same entity/page as `target`. Page targets
 * match on `page_key`; record targets match on `target_type` + `target_id`.
 */
function matchesPinTarget(pin: PinLike, target: PinTarget): boolean {
  return target.targetType === 'page'
    ? pin.target_type === 'page' && pin.page_key === target.pageKey
    : pin.target_type === target.targetType && pin.target_id === target.targetId
}

/**
 * Reactive pin toggle for a single known target. Reads the current pins list
 * and exposes whether `target` is pinned plus a `toggle` that creates or
 * deletes the pin accordingly.
 *
 * @param target - The page or record to track.
 * @returns The pinned state, a toggle action, and the in-flight flag.
 */
export function usePinTarget(target: PinTarget): UsePinTargetResult {
  const { data: pins } = useGatedPins()
  const createPin = useCreateHomepagePin()
  const deletePin = useDeleteHomepagePin()

  const existing = (pins ?? []).find((pin) => matchesPinTarget(pin, target))

  const toggle = (): void => {
    if (existing) {
      deletePin.mutate({ id: String(existing.id) })
    } else {
      createPin.mutate({
        data:
          target.targetType === 'page'
            ? { target_type: 'page', page_key: target.pageKey }
            : { target_type: target.targetType, target_id: target.targetId },
      })
    }
  }

  return {
    isPinned: Boolean(existing),
    toggle,
    isPending: createPin.isPending || deletePin.isPending,
  }
}

type ImperativePin = {
  isPinned: (target: PinTarget) => boolean
  toggle: (target: PinTarget) => boolean
}

/**
 * Imperative variant of {@link usePinTarget} for call sites where the
 * target isn't known at render time (e.g. bulk table actions keyed off
 * the currently selected row). Returns predicates/actions that accept a
 * target argument. `toggle` returns the new pinned state.
 */
export function useImperativePin(): ImperativePin {
  const { data: pins } = useGatedPins()
  // `mutate` is referentially stable across renders (react-query), so the
  // memoized object below only changes when the pins list does — keeping it
  // safe to use as a dependency in callers' useMemo.
  const { mutate: createPin } = useCreateHomepagePin()
  const { mutate: deletePin } = useDeleteHomepagePin()

  return useMemo<ImperativePin>(() => {
    const findExisting = (target: PinTarget) =>
      (pins ?? []).find((pin) => matchesPinTarget(pin, target))

    return {
      isPinned: (target) => Boolean(findExisting(target)),
      toggle: (target) => {
        const existing = findExisting(target)
        if (existing) {
          deletePin({ id: String(existing.id) })
          return false
        }
        createPin({
          data:
            target.targetType === 'page'
              ? { target_type: 'page', page_key: target.pageKey }
              : { target_type: target.targetType, target_id: target.targetId },
        })
        return true
      },
    }
  }, [pins, createPin, deletePin])
}
